"use client";

import { useEffect, useRef, useState, type ComponentType } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  Facebook,
  Instagram,
  Linkedin,
  MessageCircle,
  Share2,
  Youtube,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchAllShopSettings } from "@/store/slices/shopSettingsSlice";

type SocialIcon = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean;
  className?: string;
}>;

const XIcon = ({
  size = 16,
  className,
}: {
  size?: number;
  "aria-hidden"?: boolean;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.964 6.817H1.684l7.73-8.835L1.258 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const TikTokIcon = ({
  size = 16,
  className,
}: {
  size?: number;
  "aria-hidden"?: boolean;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const socialPlatforms: Array<{
  key: string;
  label: string;
  icon: SocialIcon;
  brandColor: string;
  hoverBg: string;
}> = [
  {
    key: "instagram",
    label: "Instagram",
    icon: Instagram,
    brandColor: "#E1306C",
    hoverBg:
      "hover:bg-gradient-to-tr hover:from-[#f09433]/15 hover:via-[#dc2743]/15 hover:to-[#bc1888]/15",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    icon: MessageCircle,
    brandColor: "#25D366",
    hoverBg: "hover:bg-emerald-500/15",
  },
  {
    key: "youtube",
    label: "YouTube",
    icon: Youtube,
    brandColor: "#FF0000",
    hoverBg: "hover:bg-red-500/15",
  },
  {
    key: "tiktok",
    label: "TikTok",
    icon: TikTokIcon,
    brandColor: "#FE2C55",
    hoverBg: "hover:bg-pink-500/15",
  },
  {
    key: "facebook",
    label: "Facebook",
    icon: Facebook,
    brandColor: "#1877F2",
    hoverBg: "hover:bg-blue-500/15",
  },
  {
    key: "twitter",
    label: "X (Twitter)",
    icon: XIcon,
    brandColor: "#1DA1F2",
    hoverBg: "hover:bg-sky-500/15",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: Linkedin,
    brandColor: "#0A66C2",
    hoverBg: "hover:bg-blue-600/15",
  },
];

const getSocialUrl = (value: unknown) => {
  if (typeof value !== "string" || !value.trim() || value.trim() === "#")
    return null;
  const url = value.trim();
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export default function ShopFloatingSocialLinks() {
  const dispatch = useAppDispatch();
  const { data: shopSettingsList } = useAppSelector(
    (state) => state.shopSettings,
  );
  const { data: generalSettings } = useAppSelector(
    (state) => state.landing.generalSettings,
  );
  const [open, setOpen] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!shopSettingsList || shopSettingsList.length === 0) {
      dispatch(fetchAllShopSettings());
    }
  }, [dispatch, shopSettingsList]);

  const shopSocialLinks = shopSettingsList?.[0]?.socialLinks;
  const fallbackSocialLinks = generalSettings?.socialLinks;
  const activeSocialLinks =
    shopSocialLinks &&
    Object.values(shopSocialLinks).some((v) =>
      Boolean(v && typeof v === "string" && v.trim() && v.trim() !== "#"),
    )
      ? shopSocialLinks
      : fallbackSocialLinks;

  const availableLinks = socialPlatforms.filter(({ key }) => {
    return Boolean(
      getSocialUrl(activeSocialLinks?.[key as keyof typeof activeSocialLinks]),
    );
  });

  useEffect(() => {
    const hoverQuery = window.matchMedia("(hover: hover)");
    const updateHoverSupport = () => setSupportsHover(hoverQuery.matches);
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!widgetRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    updateHoverSupport();
    hoverQuery.addEventListener("change", updateHoverSupport);
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      hoverQuery.removeEventListener("change", updateHoverSupport);
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  if (availableLinks.length === 0) return null;

  return (
    <div
      ref={widgetRef}
      onMouseEnter={() => supportsHover && setOpen(true)}
      onMouseLeave={() => supportsHover && setOpen(false)}
      className="fixed top-1/2 left-0 z-40 -translate-y-1/2 select-none"
    >
      <div className="flex flex-col items-stretch overflow-visible rounded-r-2xl border border-l-0 border-purple-200/90 bg-white/95 shadow-[0_10px_35px_-5px_rgba(139,49,255,0.18),0_4px_12px_rgba(0,0,0,0.06)] backdrop-blur-2xl transition-all duration-300 max-h-[85vh] overflow-y-auto no-scrollbar">
        <AnimatePresence>
          {open && (
            <motion.nav
              aria-label="Shop Social Media Links"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.22 }}
              className="flex flex-col gap-1 sm:gap-1.5 p-1 sm:p-1.5"
            >
              {availableLinks.map(
                ({ key, label, icon: Icon, brandColor, hoverBg }, index) => {
                  const href = getSocialUrl(
                    activeSocialLinks?.[key as keyof typeof activeSocialLinks],
                  );

                  return (
                    <div
                      key={key}
                      className="relative group/item flex items-center"
                    >
                      <motion.a
                        href={href || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit our ${label} page`}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: reduceMotion ? 0 : index * 0.03 }}
                        whileHover={{ scale: 1.12, x: 2 }}
                        whileTap={{ scale: 0.92 }}
                        style={{ color: brandColor }}
                        className={`flex h-8 w-8 sm:h-10 sm:w-10 md:h-11 md:w-11 items-center justify-center rounded-lg sm:rounded-xl transition-all duration-200 cursor-pointer bg-gray-50/90 border border-gray-100 hover:border-purple-300 hover:bg-white shadow-xs ${hoverBg}`}
                      >
                        <span className="block sm:hidden">
                          <Icon size={15} aria-hidden={true} />
                        </span>
                        <span className="hidden sm:block">
                          <Icon size={18} aria-hidden={true} />
                        </span>
                      </motion.a>

                      {/* Slide-out Tooltip Badge (Desktop/Tablet) */}
                      <div className="pointer-events-none absolute left-full ml-3 whitespace-nowrap rounded-xl px-3 py-1.5 text-xs font-bold shadow-xl border border-purple-100 bg-white/95 text-gray-800 shadow-purple-900/10 backdrop-blur-md transition-all duration-200 opacity-0 -translate-x-2 group-hover/item:opacity-100 group-hover/item:translate-x-0 hidden md:flex items-center gap-2 z-50">
                        <span
                          className="w-2 h-2 rounded-full shadow-xs"
                          style={{ backgroundColor: brandColor }}
                        />
                        <span>{label}</span>
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 border-l border-b bg-white border-purple-100" />
                      </div>
                    </div>
                  );
                },
              )}
            </motion.nav>
          )}
        </AnimatePresence>

        {/* Bottom Toggle Share Button */}
        <button
          type="button"
          aria-label={
            open ? "Close shop social links" : "Open shop social links"
          }
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className={`flex h-8 w-8 sm:h-10 sm:w-10 md:h-11 md:w-11 items-center justify-center transition-all cursor-pointer focus:outline-none rounded-r-xl ${
            open
              ? "bg-gradient-to-br from-[#8B31FF] to-[#6D28D9] text-white shadow-md shadow-purple-500/25"
              : "bg-purple-50 text-[#8B31FF] hover:bg-purple-100"
          }`}
          title={open ? "Collapse socials" : "Explore our socials"}
        >
          <Share2
            size={16}
            className={`transition-transform duration-300 ${open ? "rotate-90 text-white" : "text-[#8B31FF]"}`}
            aria-hidden={true}
          />
        </button>
      </div>
    </div>
  );
}
